import React, { type PropsWithChildren, useCallback, useMemo } from "react";
import { twMerge } from "tailwind-merge";

import { type CommonColors } from "../../../types/colors";
import { useControlledState } from "../../../hooks/useControlledState";

import { Context } from "./context";

export interface AccordionProps {
  collapsible?: boolean;
  expanded?: boolean;
  defaultExpanded?: boolean;
  onChange?: (expanded: boolean) => void;
  className?: string;
  disabled?: boolean;
  variant?: CommonColors;
}

export function SupAccordion({
  collapsible = true,
  expanded: isExpanded,
  defaultExpanded,
  onChange,
  className,
  disabled = false,
  variant,
  children,
}: PropsWithChildren<AccordionProps>) {
  const [expanded = false, setExpanded] = useControlledState({ value: isExpanded, defaultValue: defaultExpanded, onStateChange: onChange });

  const handleToggle = useCallback(() => {
    setExpanded(!expanded);
  }, [setExpanded, expanded]);

  const contextValues = useMemo(
    () => ({
      defaultExpanded,
      variant,
      disabled,
      expanded: collapsible ? expanded && !disabled : true,
      collapsible,
      toggle: !disabled && collapsible ? handleToggle : undefined,
    }),
    [defaultExpanded, collapsible, expanded, disabled, variant, handleToggle]
  );

  return (
    <Context.Provider value={contextValues}>
      <div className={twMerge("sup-accordion transition-opacity", disabled && "opacity-44", className)}>{children}</div>
    </Context.Provider>
  );
}
